<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('client.title') }}
        </h6>

        <CreateButton
            :label="$t('client.create')"
            :to="{ name: 'clientForm' }">
        </CreateButton>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('client.list') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :clientDropdown="clientDropdown"
            :officeDropdown="officeDropdown"
            @search="getClients"/>

        <PerPage
            :values="[10, 25, 50, 100]"
            @update:perPage="getClients"/>

        <ExcelExportButton
            style="margin-right: 15px;"
            :label="$t('general.excel_export')"
            :loading="isExcelLoaded"
            @click="getExcelExport"/>

        <EmptyState
            v-if="!clients?.data?.length"
            icon="fa-building"
            title="Keine Kostenträger gefunden"
            subtitle="Es wurden keine Kostenträger gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>{{ $t('general.client_number') }}</th>
              <th>{{ $t('general.client') }}</th>
              <th>{{ $t('general.address_1') }}</th>
              <th>{{ $t('general.address_2') }}</th>
              <th>{{ $t('general.postal_code') }}</th>
              <th>{{ $t('general.city') }}</th>
              <th>{{ $t('general.location') }}</th>
              <th style=" text-align: right; padding-right: 30px; min-width: 150px;">{{ $t('general.actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(client) in clients?.data" :key="client.id">
              <td>{{ client?.customerNumber }}</td>
              <td>{{ client?.name }}</td>
              <td>{{ client?.addressFirst }}</td>
              <td>{{ client?.addressSecond }}</td>
              <td>{{ client?.zip }}</td>
              <td>{{ client?.city }}</td>
              <td>{{ client?.registrationOffice }}</td>
              <td class="text-right">
                <button class="btn btn-primary"
                        @click="router.push({name: 'clientForm', params: {id: client?.id} })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ml-2"
                        @click="deleteModal(client?.id!)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="clients?.pagination?.total_pages ?? 0 > 1"
            :totalPages="clients?.pagination?.total_pages"
            :currentPage="clients?.pagination?.current_page"
            :totalItems="clients?.pagination?.total_items"
            :perPage="clients?.pagination?.items_per_page"
            @pagechanged="getClients"/>
      </div>
    </div>

    <ModalDelete
        v-model="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="deleteClient">
    </ModalDelete>

  </PageTemplate>
</template>


<script setup lang="ts">
import clientService from '@/api/Client';
import Pagination from '@/components/Pagination.vue';
import router from '@/router';
// @ts-ignore
import {getClientDropdown, getOfficeDropdown} from '@/shared/dropdowns';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import Search from '@/views/App/Client/Search.vue';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import PerPage from '@/components/PerPage.vue';
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {downloadFile} from "@/shared/functions";
import {IClientFilters, IClientList} from "@/interfaces/Client/IClient";
import {ISelectOption} from "@/interfaces/Course/SocioTherapy/ICoursePrice";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const {t} = useI18n();
const filters = ref<IClientFilters>({
  clientId: '',
  officeId: '',
  sortBy: '',
  page: 1,
  limit: 10,
});

const clients = ref<IClientList>();
const clientId = ref(0);
const officeDropdown = ref<ISelectOption[]>([])
const clientDropdown = ref<ISelectOption[]>([])
const isSearching = ref(false);
const showModal = ref(false);
const isLoaded = ref(false);
const isExcelLoaded = ref(false);

async function getClients(
    page: number | undefined = 1,
    perPage?: number,
    passedFilters?: IClientFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = clients.value?.pagination?.items_per_page;

  try {
    const {data} = await clientService.getClients({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes!,
    });

    clients.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function deleteClient() {
  try {
    await clientService.deleteClient(clientId.value);

    filters.value.clientId = '';
    filters.value.locationId = '';
    showModal.value = false;

    await getClients();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function deleteModal(id: number) {
  CloseToast();
  clientId.value = id;
  showModal.value = true;
}

async function getExcelExport() {
  await downloadFile('/client/excel-export',
      undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  await Promise.all([
    getClients(),
    getClientDropdown(clientDropdown),
    getOfficeDropdown(officeDropdown),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>

</style>